import { EventEmitter, Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { Observable, of } from "rxjs";
import { map } from "rxjs/operators";
import { LocalStorageService, SessionStorageService } from "ngx-webstorage";

import { ApplicationConfigService } from "../config/application-config.service";
import { Login } from "../models/login";
import { Account } from '../models/account.model';

type JwtToken = {
    id_token: string;
};

@Injectable({ providedIn: "root" })
export class AuthServerProvider {
    constructor(
        private http: HttpClient,
        private $localStorage: LocalStorageService,
        private $sessionStorage: SessionStorageService,
        private applicationConfigService: ApplicationConfigService
    ) { }

    getToken(): string {
        const tokenInLocalStorage: string | null = this.$localStorage.retrieve(
            "authenticationToken"
        );
        const tokenInSessionStorage: string | null =
            this.$sessionStorage.retrieve("authenticationToken");
        return tokenInLocalStorage ?? tokenInSessionStorage ?? "";
    }

    getCurrentUser(): Observable<any> {
        const token = this.getToken();
        if (token) {
            const encodedPayload = token.split('.')[1];
            const payload = window.atob(encodedPayload);

            return of(JSON.parse(payload));
        } else {
            return of(undefined);
        }
    }

    login(credentials: Login): Observable<void> {
        return this.http
            .post<JwtToken>(
                this.applicationConfigService.getEndpointFor(
                    "api/authenticate"
                ),
                credentials
            ).pipe(
                map((response) =>
                    this.authenticateSuccess(response, credentials.rememberMe)
                )
            )
    }

    logout(): Observable<void> {
        return new Observable((observer) => {
            this.$localStorage.clear("authenticationToken");
            this.$sessionStorage.clear("authenticationToken");
            observer.complete();
        });
    }

    loginSocial(credentials): Observable<any> {
        const data = {
            username: credentials.username,
            password: credentials.password,
            rememberMe: credentials.rememberMe,
        };
        return this.http
            .post<JwtToken>(
                this.applicationConfigService.getEndpointFor(
                    "api/authenticate-social"
                ),
                data
            )
            .pipe(
                map((response) =>
                    this.authenticateSuccess(response, credentials.rememberMe)
                )
            );
    }

    private authenticateSuccess(response: JwtToken, rememberMe: boolean): void {
        const jwt = response.id_token;
        if (rememberMe) {
            this.$localStorage.store("authenticationToken", jwt);
            this.$sessionStorage.clear("authenticationToken");
        } else {
            this.$sessionStorage.store("authenticationToken", jwt);
            this.$localStorage.clear("authenticationToken");
        }
    }
}
